// Movement for the bot: hexes and states as numbers, guard zones, where a unit can get to in one
// activation (by facing, stopped by enemy zones) and searches over many activations. Pure helpers
// over the engine's rules; the evaluation (ai.ts) and the route maps (routes.ts) build on them.

import { Hex, ahead } from '../board';
import { zoneHexes, zoneUp, REVERSE_COST } from '../rules';
import { typeOf, hasTrait, turnOffset } from '../units';
import type { CtfState, Team, Unit } from '../types';

// ── Movement ─────────────────────────────────────────────────────────────────
// Hexes and states are numbers in the hot loops: hid = (q + 32) * 64 + (r + 32); a state (hex and
// facing) is hid * 8 + facing.

// A step of a path: `cost` is the Move it takes (1 for a step ahead or a turn; 2 for a slow turn or a
// Reverse step backward).
export type Step = { kind: 'move'; to: Hex; cost: number } | { kind: 'turn'; dir: 1 | -1; cost: number };
// The Move a path costs, and how many of its steps `spent` Move has paid for (-1: not a step boundary).
export const pathCost = (path: Step[]) => path.reduce((a, st) => a + st.cost, 0);
export function stepsPaid(path: Step[], spent: number): number {
  let m = 0;
  for (let i = 0; i <= path.length; i++) {
    if (m === spent) return i;
    if (i < path.length) m += path[i].cost;
  }
  return -1;
}
export interface EndState { pos: Hex; facing: number; path: Step[]; stopped: boolean }

export const hid = (h: Hex) => (h.q + 32) * 64 + (h.r + 32);
export const DSTEP = [{ q: 1, r: 0 }, { q: 1, r: -1 }, { q: 0, r: -1 }, { q: -1, r: 0 }, { q: -1, r: 1 }, { q: 0, r: 1 }].map(d => d.q * 64 + d.r);

// A guard zone as hid offsets from the unit at facing f (cached per unit type: the config, and with it
// the squads, is shared by every state of a game).
export const zoneCache = new WeakMap<object, number[][]>();
export function zoneOffsets(s: CtfState, u: Unit, f: number): number[] {
  const type = typeOf(s, u);
  let byFacing = zoneCache.get(type);
  if (!byFacing) {
    byFacing = [0, 1, 2, 3, 4, 5].map(face => type.zone.map(o => { const [q, r] = turnOffset(o, face); return q * 64 + r; }));
    zoneCache.set(type, byFacing);
  }
  return byFacing[f];
}
// Enemy zones that stop `u` (none for a slippery unit).
export const stopsFor = (s: CtfState, u: Unit, stops: Set<number>) => (hasTrait(s, u, 'slippery') ? NO_STOPS : stops);
export const NO_STOPS = new Set<number>();

// Which hids are on the map (cached per map size).
export const onMapCache = new Map<number, Uint8Array>();
export function onMapTable(s: CtfState): Uint8Array {
  let t = onMapCache.get(s.cells.length);
  if (!t) {
    t = new Uint8Array(64 * 64);
    for (const c of s.cells) for (const h of c.hexes) t[hid(h)] = 1;
    onMapCache.set(s.cells.length, t);
  }
  return t;
}

// Hexes covered by the zones of `team`'s units: the ones that are up (stop movement now), or all
// (`skip`: a unit index to leave out).
export function zoneSet(s: CtfState, team: Team, onlyUp: boolean, skip = -1): Set<number> {
  const out = new Set<number>();
  for (let i = 0; i < s.units.length; i++) {
    const e = s.units[i];
    if (i === skip || e.team !== team || e.inPrison || !e.pos || (onlyUp && !zoneUp(s, e))) continue;
    for (const z of zoneHexes(s, e)) out.add(hid(z));
  }
  return out;
}

// Every end state a unit can reach with `moves` points of Move, by the cheapest path: a step ahead
// costs 1, a 60° turn 1 (2 with slow turns), a Reverse unit's step straight backward 2;
// moving into a hex in `stops` ends its movement. Bodies in the way block it.
export function reach(s: CtfState, u: Unit, moves: number, stops: Set<number>): EndState[] {
  const map = onMapTable(s);
  const bodies = new Set(s.units.filter(x => x.id !== u.id && x.pos).map(x => hid(x.pos!)));
  const turnCost = hasTrait(s, u, 'slowTurns') ? 2 : 1;
  const reverse = hasTrait(s, u, 'reverse');
  stops = stopsFor(s, u, stops);
  type Node = EndState & { spent: number };
  const start: Node = { pos: u.pos!, facing: u.facing, path: [], stopped: false, spent: 0 };
  const best = new Map<number, number>();               // state → Move spent getting there
  const ended = new Set<number>();                       // end states (hex, facing) already listed
  const out: EndState[] = [];
  const buckets: Node[][] = [[start]];
  for (let m = 0; m < buckets.length; m++) {
    const bucket = buckets[m] ?? [];
    for (let i = 0; i < bucket.length; i++) {
      const st = bucket[i];
      const key = hid(st.pos) * 8 + st.facing;
      if (best.has(key)) continue;                       // buckets go by Move spent: the first visit is the cheapest
      best.set(key, st.spent);
      const endKey = hid(st.pos) * 8 + st.facing;
      if (!ended.has(endKey)) { ended.add(endKey); out.push({ pos: st.pos, facing: st.facing, path: st.path, stopped: st.stopped }); }
      if (st.stopped) continue;
      const add = (n: Node) => { (buckets[n.spent] ??= []).push(n); };
      const left = moves - st.spent;
      const to = ahead(st.pos, st.facing), th = hid(to);
      if (left >= 1 && map[th] && !bodies.has(th)) {
        add({ pos: to, facing: st.facing, path: [...st.path, { kind: 'move', to, cost: 1 }], stopped: stops.has(th), spent: st.spent + 1 });
      }
      if (reverse && left >= REVERSE_COST) {
        const back = ahead(st.pos, (st.facing + 3) % 6), bh = hid(back);
        if (map[bh] && !bodies.has(bh)) add({ pos: back, facing: st.facing, path: [...st.path, { kind: 'move', to: back, cost: REVERSE_COST }], stopped: stops.has(bh), spent: st.spent + REVERSE_COST });
      }
      if (left >= turnCost) {
        for (const dir of [1, -1] as const) {
          add({ pos: st.pos, facing: (st.facing + dir + 6) % 6, path: [...st.path, { kind: 'turn', dir, cost: turnCost }], stopped: false, spent: st.spent + turnCost });
        }
      }
    }
  }
  return out;
}

// A visited set over end states for the searches below: a state is seen when its entry equals the
// current search's stamp, so starting a new search is just a new stamp.
export const seenAt = new Int32Array(64 * 64 * 8);
const freeAt = new Int32Array(64 * 64 * 8);
let stampNow = 0;
export const newStamp = () => ++stampNow;
// The same for hexes, so an `asked` list (below) holds each hex once.
export const askedAt = new Int32Array(64 * 64);

// End states (as numbers) reachable from several start states at once with `moves` points of Move,
// not already seen under `stamp` (which it marks). No bodies: for route lengths and threats.
// `asked` collects every hex whose stop status the search looked at: under any stops that agree on
// those hexes, the search runs exactly the same.
export function reachFrom(map: Uint8Array, moves: number, stops: Set<number>, sources: number[], stamp: number, asked?: number[], askStamp = 0): number[] {
  const out: number[] = [];
  let frontier: number[] = [];
  // A state can be reached stopped (stepped into a stopping hex) and still be reached free to move on
  // (turned into, by a unit that started on that hex), so the two are marked apart: seenAt for
  // "reached", freeAt for "moves on from here".
  for (const src of sources) { seenAt[src] = stamp; freeAt[src] = stamp; frontier.push(src); }
  const free = (k: number, next: number[]) => {
    if (freeAt[k] === stamp) return;
    freeAt[k] = stamp;
    if (seenAt[k] !== stamp) { seenAt[k] = stamp; out.push(k); }
    next.push(k);
  };
  for (let m = 0; m < moves && frontier.length; m++) {
    const next: number[] = [];
    for (const st of frontier) {
      const h = st >> 3, f = st & 7;
      const to = h + DSTEP[f];
      if (map[to]) {
        const k = to * 8 + f;
        if (asked && seenAt[k] !== stamp && askedAt[to] !== askStamp) { askedAt[to] = askStamp; asked.push(to); }
        if (!stops.has(to)) free(k, next);
        else if (seenAt[k] !== stamp) { seenAt[k] = stamp; out.push(k); }
      }
      free(h * 8 + (f + 1) % 6, next);
      free(h * 8 + (f + 5) % 6, next);
    }
    frontier = next;
  }
  return out;
}

